import {Injectable}                     from '@angular/core';

import {Message}                        from './message';
import {MessageEntry}                   from "./message-entry";

import {isNullOrUndefined}              from "util";
import {Observable, BehaviorSubject}    from 'rxjs';

/**
 * Display a message window at the top of the story search
 * screen.
 *
 * Each Message object contains one or more MessageEntry objects.
 * When the Message is told to show itself, it finds the MessageEntry
 * with the highest "level" value (or the latest one with the highest
 * "level" value, if more than one exists) and displays the message
 * contents in that MessageEntry.
 *
 * This allows the Message object to be created at the beginning of
 * an action and to accumulate all MessageEntry objects while the
 * action is progressing and allow the most important message to be
 * shown when the action cycle is completed.
 *
 * Doing it this way allows a high-ranked error message to be displayed
 * even though the last part of the action may have completed successfully.
 *
 */
@Injectable()
export class MessageService {


	/**
	 * Initialize the BehaviorSubject.  BehaviorSubjects always have to
	 * have an initial "prime-the-pump" message.
	 *
	 * @type {BehaviorSubject<Message>}
	 * @private The initialized BehaviorSubject<Message> stream.
	 */
	private _subject: BehaviorSubject<Message> = new BehaviorSubject<Message>(new Message(new MessageEntry({

		message: 'Loading...',
		level: 1,
		linkText: null,
		link: null

	})));


	/**
	 * Expose the stream of Message objects as an Observable that
	 * can be subscribed to by objects wishing to send messages.
	 *
 	 * @type {Observable<Message>}
	 */
	public readonly subject: Observable<Message> = this._subject.asObservable();


	/**
	 * Read an array of MessageEntry objects, merge their content into
	 * one message and send that message.  The error level
	 * will be equal to the highest level in the array.
	 * If there is more than one link, the link displayed
	 * will be the one for the last message that had a link
	 * entry.
	 *
	 * @param {MessageEntry[]} messages  The array of messages to merge and display.
	 */
	mergeMessagesAndSend(messages: MessageEntry[]): void {

		if (0 === messages.length) {

			return;

		}

		let linkText: string = null;
		let link: string = null;
		let level: number = 0;
		let merged: string = '';

		for (let message of messages) {

			if (message.level > level) {

				level = message.level;

			}

			if (! (isNullOrUndefined(message.linkText) || isNullOrUndefined(message.link))) {

				link = message.link;
				linkText = message.linkText;

			}

			if (0 !== merged.length) {

				merged = merged + '.';

			}

			merged = merged + ' ' + message.message.trim();

		}

		this.sendMessageContent(merged, level, linkText, link);

	}


	/**
	 * Send the Message to subscribers.
	 *
	 * @param message  A Message object to be sent to all subscribers.
	 */
	sendMessage(message: Message):void {

		this._subject.next(message);

	}


	/**
	 * Send a Message to all subscribers that contains
	 * a single MessageEntry object.
	 *
	 * @param entry  An initialized MessageEntry.
	 */
	sendMessageEntry(entry: MessageEntry):void {

		// Wrap the MessageEntry in a Message and send it.
		let message: Message = new Message(entry);
		this.sendMessage(message);

	}


	/**
	 * Send a Message that contains a single MessageEntry
	 * made up of the parameters supplied.
	 *
	 * @param message  The text of the message that will be displayed.
	 * @param level    The numeric level of the error message (0 - 4).
	 * @param linkText The text of a link to be displayed with the message text. (May be null.)
	 * @param link     The URL of a link to be displayed with the link text. (May be null.)
	 */
	sendMessageContent(message: string, level: number, linkText: string, link: string): void {

		let entry: MessageEntry = new MessageEntry();
		entry.message = message;
		entry.level = level;
		entry.linkText = linkText;
		entry.link = link;
		this.sendMessageEntry(entry);

	}

}



// WEBPACK FOOTER //
// ./src/app/services/message/message.service.ts